import React, { useEffect, useState } from 'react';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { ReactComponent as CSVIcon } from 'assets/icons/csv-file.svg';
import styles from './CSVIntegrations.module.css';
import { ReactComponent as TableIcon } from '../../../icons/table.svg';
import { ContactSuppressionHeader } from '../ContactSuppressions/ContactSuppressionHeader';
import {
  CaptureActionEnum,
  SuppressionSettingsCSVModel,
} from '@mm/state/model/SuppressionSettingsModel';
import { useSuppressions } from '../ContactSuppressions/use-suppressions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { IndexAttributeNameMap } from '@mm/state/modules/modalsState/props';
import { csvExampleBody } from '../ContactSuppressions/ContactSuppression';
import {
  errorToast,
  successToast,
} from '../../../../../../../../common/helpers/toast';
import SuppressionSettingsController from '@mm/state/controllers/SuppressionSettingsController';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useSelector } from 'react-redux';
import ContactsController from '@mm/state/controllers/ContactsCotroller';
import { ContactsSuppresstionsLists } from '../ContactSuppressions/ContactsSuppresstionsLists';
import { landmineNames } from '@mm/constants/landmine-names';
import { BaseIntegrationEmptyState } from '../../../BaseIntegration/BaseIntegrationEmptyState';

export const CSVIntegrations = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const [uploadedCSVFiles, setUploadedCSVFiles] = useState<
    SuppressionSettingsCSVModel[] | undefined
  >([]);
  const { fetchCsvFiles, mapCSVFiles } = useSuppressions();
  const { openImportContactListModal } = useModalActions();

  const csvExampleName = 'contact-suppression-list.csv';

  const csvs = mapCSVFiles(uploadedCSVFiles);
  const csvFiles = [...csvs];

  const csvSuppressionLists = csvFiles.filter(
    item =>
      item.type === 'csv_file' &&
      item.captureAction === CaptureActionEnum.do_not_send,
  );

  const csvUnsubscribeLists = csvFiles.filter(
    item =>
      item.type === 'csv_file' &&
      item.captureAction === CaptureActionEnum.do_not_capture,
  );

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_CONTACT_SUPPRESSION,
    featureFlag: 'contactSuppression',
  };

  const handleManualUploadSubmit = async (
    {
      facebookPageId,
      file,
      mapping,
      isFirstRowHeader,
    }: {
      facebookPageId: number;
      file: File;
      mapping: IndexAttributeNameMap;
      isFirstRowHeader: boolean;
    },
    setLoading: (v: boolean) => void,
    setSuccess: (v: boolean) => void,
    captureAction: CaptureActionEnum,
  ) => {
    setLoading(true);
    try {
      const csv = await SuppressionSettingsController.createCSV(
        facebookPageId,
        {
          fileName: file.name,
          sourceSize: file.size,
          mapping,
          headersAtFirstLine: isFirstRowHeader,
          captureAction,
        },
      );

      if (csv?.id && csv?.uploadUrl) {
        const { uploadUrl, id } = csv;
        const uploadResponse = await ContactsController.uploadContactsCsvFile(
          uploadUrl,
          file,
        );

        if (uploadResponse.status !== 200) {
          throw new Error(file.name);
        }

        await SuppressionSettingsController.updateCSV(id, {
          uploaded: true,
        });

        successToast('CSV Uploaded');
        setSuccess(true);
        await retrieveCSV(captureAction);
      }
    } catch (err) {
      errorToast(
        `Error uploading csv: ${err?.message ? err.message.toString() : ''}`,
        'top-right',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUploadClick = (captureAction: CaptureActionEnum) => {
    openImportContactListModal({
      title: 'Upload Contact Suppression CSV Manually',
      helpLink: false,
      withMapper: false,
      csvExampleBody,
      csvExampleName,
      onSubmit: (
        { file, mapping, isFirstRowHeader },
        setLoading,
        setSuccess,
      ) => {
        if (!facebookPageId) return Promise.resolve();
        return handleManualUploadSubmit?.(
          { facebookPageId, file, mapping, isFirstRowHeader },
          setLoading,
          setSuccess,
          captureAction,
        );
      },
    });
  };

  const retrieveCSV = async (captureAction: CaptureActionEnum) => {
    const files = await fetchCsvFiles(captureAction, 'csv_file');
    setUploadedCSVFiles(files.csvs);
  };

  useEffect(() => {
    retrieveCSV(CaptureActionEnum.do_not_send);
    retrieveCSV(CaptureActionEnum.do_not_capture);
  }, []);

  return (
    <div className={styles.container}>
      <BaseIntegration
        openOnMount
        titleComponent={
          <BaseIntegrationHeader
            title="CSV"
            subtitle="Upload CSV Manually"
            icon={<CSVIcon />}
            haveErrors={csvFiles.some(csv => csv?.processed === false)}
            css={{ icon: 'integrations__header-icon-csv' }}
            {...sectionProps}
          />
        }
      >
        <div className={styles.contentWrapper}>
          <div className={styles.label}>Sources</div>
          <div className={styles.content}>
            <ContactSuppressionHeader
              isSuppression
              count={csvSuppressionLists?.length}
              onClick={() =>
                handleCSVUploadClick(CaptureActionEnum.do_not_send)
              }
            />

            {csvSuppressionLists.length ? (
              <ContactsSuppresstionsLists
                data={csvSuppressionLists}
                onSuccess={() => retrieveCSV(CaptureActionEnum.do_not_send)}
              />
            ) : (
              <BaseIntegrationEmptyState
                button="Add a CSV Suppression List"
                onClick={() =>
                  handleCSVUploadClick(CaptureActionEnum.do_not_send)
                }
                icon={<TableIcon />}
                {...sectionProps}
              />
            )}
          </div>
          <div className={styles.content}>
            <ContactSuppressionHeader
              isSuppression={false}
              count={csvUnsubscribeLists?.length}
              onClick={() =>
                handleCSVUploadClick(CaptureActionEnum.do_not_capture)
              }
            />

            {csvUnsubscribeLists.length ? (
              <ContactsSuppresstionsLists
                data={csvUnsubscribeLists}
                onSuccess={() => retrieveCSV(CaptureActionEnum.do_not_capture)}
              />
            ) : (
              <BaseIntegrationEmptyState
                button="Add a CSV Unsubscribe List"
                onClick={() =>
                  handleCSVUploadClick(CaptureActionEnum.do_not_capture)
                }
                icon={<TableIcon />}
                {...sectionProps}
              />
            )}
          </div>
        </div>
      </BaseIntegration>
    </div>
  );
};
